import { FieldStatusEnum, getFieldStatus, usePecField } from 'components/form'
import { isEqualWith } from 'lodash'
import { useMemo } from 'react'
import { MetaArray } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { RowType } from 'view/atendimentos/detail/components/EditableList'

interface ListFieldStatus<T> {
  fieldStatus: Map<T, FieldStatusEnum>
  disabledValues: T[]
}

interface UseListFieldStatusParams<T> {
  name: MetaArray<T>
  controlModifiedStatus?: boolean
  isSameRow(itemA: T, itemB: T): boolean
}

export function useListFieldStatus<T extends RowType>(params: UseListFieldStatusParams<T>): ListFieldStatus<T> {
  const { name, isSameRow, controlModifiedStatus = false } = params

  const { input, meta } = usePecField<T[]>({ name })

  const initialValues: T[] | undefined = meta.initial
  const currentValues: T[] = input.value

  return useMemo(() => {
    if (!controlModifiedStatus || !Array.isArray(currentValues)) return { fieldStatus: new Map(), disabledValues: [] }

    const newStatusMap = new Map<T, FieldStatusEnum>(
      initialValues?.map((initialValueItem) => {
        const currentValueItem = currentValues.find((item) => isSameRow(initialValueItem, item))

        const dirty = isDirty(initialValueItem, currentValueItem)
        const status = getFieldStatus(initialValueItem, currentValueItem, isEmpty, dirty)

        return [currentValueItem ?? initialValueItem, status]
      })
    )

    currentValues?.forEach((currentValueItem) => {
      const initialValueItem = initialValues?.find((initialValueItem) => isSameRow(currentValueItem, initialValueItem))

      if (!initialValueItem)
        newStatusMap.set(currentValueItem, getFieldStatus(initialValueItem, currentValueItem, isEmpty))
    })

    return { fieldStatus: newStatusMap, disabledValues: Array.from(newStatusMap.keys()) }
  }, [controlModifiedStatus, currentValues, initialValues, isSameRow])
}

const isDirty = <T extends RowType>(initialValue: T | undefined, currentValue: T | undefined) =>
  !isEqualWith(initialValue, currentValue, (_initialValueItem, _currentValueItem, key) => {
    return key === '_id' ? true : undefined
  })
